/**
 * Fallback canónico del funnel intl (USExclusiveLanding → DualCTAVideoLanding).
 *
 * Cuando admin no configuró variante DEFAULT (o faltan campos), el visitor
 * igual ve Mux + CTA "Exclusive Membership" y el checkout Stripe redirige a
 * `/portal/complete-profile` para completar perfil Clerk.
 *
 * Fuente única: frontend, API labels, experience-gaps, admin copy.
 */

import { isValidMuxPlaybackId } from "./muxPlaybackId.js";
import {
  STRIPE_DUAL_CTA_VARIANT_CONTRACT,
  STRIPE_EXCLUSIVE_MEMBERSHIP_PRODUCT_NAME,
  STRIPE_EXCLUSIVE_MEMBERSHIP_PROMOTION_CODE_ID,
} from "./stripeBrandContract.js";

/** Label VIP default (botón hero / dual CTA intl). */
export const INTL_DUAL_CTA_DEFAULT_VIP_LABEL_ES = "Membresía Exclusiva" as const;
export const INTL_DUAL_CTA_DEFAULT_VIP_LABEL_EN = "Exclusive Membership" as const;

/** Post-pago Stripe → completar perfil → /portal (aprobación). */
export const INTL_DUAL_CTA_POST_PAYMENT_PATH = "/portal/complete-profile" as const;

/** Mux hero cuando variante admin no tiene playback (seed migration 046). */
export const INTL_DUAL_CTA_DEFAULT_MUX_PLAYBACK_ID =
  "JoJ2wxepBQf65hfnbQfs1cC5Kdaz8BtrK9oBZ6Ie85Y" as const;

const DEFAULT_CONTRACT =
  STRIPE_DUAL_CTA_VARIANT_CONTRACT.find((v) => v.variantId === "vip_exclusive_default") ??
  STRIPE_DUAL_CTA_VARIANT_CONTRACT[0];

export const INTL_DUAL_CTA_DEFAULT_PRICE_USD = DEFAULT_CONTRACT.priceUsd;
export const INTL_DUAL_CTA_DEFAULT_STRIPE_PRICE_ID = DEFAULT_CONTRACT.stripePriceId;
export const INTL_DUAL_CTA_DEFAULT_STRIPE_PRODUCT_ID = DEFAULT_CONTRACT.stripeProductId;
export const INTL_DUAL_CTA_DEFAULT_STRIPE_PROMOTION_CODE_ID =
  STRIPE_EXCLUSIVE_MEMBERSHIP_PROMOTION_CODE_ID;

/** variant_id canónico cuando worker/DB no asignaron variante A/B. */
export const INTL_DUAL_CTA_DEFAULT_VARIANT_ID = DEFAULT_CONTRACT.variantId;

export type IntlDualCtaFallbackSource = "variant" | "env" | "contract" | "default";

export interface IntlDualCtaFallbackInput {
  variantMuxPlaybackId?: string | null;
  variantPriceUsd?: number | null;
  variantStripePriceId?: string | null;
  variantStripeProductId?: string | null;
  variantVipLabelEs?: string | null;
  variantVipLabelEn?: string | null;
  envMuxPlaybackId?: string | null;
  envPriceUsd?: number | null;
}

export interface IntlDualCtaResolvedFallback {
  vipLabelEs: string;
  vipLabelEn: string;
  muxPlaybackId: string;
  priceUsd: number;
  stripePriceId: string | null;
  stripeProductId: string | null;
  stripePromotionCodeId: string;
  stripeProductName: string;
  postPaymentPath: string;
  /** variant_id para metadata Stripe cuando no hay assignment activo. */
  defaultVariantId: string;
  sources: {
    vipLabel: IntlDualCtaFallbackSource;
    muxPlaybackId: IntlDualCtaFallbackSource;
    priceUsd: IntlDualCtaFallbackSource;
    stripePriceId: IntlDualCtaFallbackSource;
  };
  /** Campos que admin debería completar en /admin/dual-cta-variants (sin variant). */
  adminSetupHints: string[];
}

function pickMux(
  variant?: string | null,
  env?: string | null,
): { value: string; source: IntlDualCtaFallbackSource } {
  const v = (variant ?? "").trim();
  if (isValidMuxPlaybackId(v)) return { value: v, source: "variant" };
  const e = (env ?? "").trim();
  if (isValidMuxPlaybackId(e)) return { value: e, source: "env" };
  return { value: INTL_DUAL_CTA_DEFAULT_MUX_PLAYBACK_ID, source: "contract" };
}

function pickPrice(
  variant?: number | null,
  env?: number | null,
): { value: number; source: IntlDualCtaFallbackSource } {
  if (typeof variant === "number" && variant > 0) {
    return { value: variant, source: "variant" };
  }
  if (typeof env === "number" && env > 0) {
    return { value: env, source: "env" };
  }
  return { value: INTL_DUAL_CTA_DEFAULT_PRICE_USD, source: "contract" };
}

function pickStripePriceId(variant?: string | null): {
  value: string | null;
  source: IntlDualCtaFallbackSource;
} {
  const v = (variant ?? "").trim();
  if (/^price_[A-Za-z0-9]+$/.test(v)) {
    return { value: v, source: "variant" };
  }
  return { value: INTL_DUAL_CTA_DEFAULT_STRIPE_PRICE_ID, source: "contract" };
}

function pickVipLabels(
  variantEs?: string | null,
  variantEn?: string | null,
): { es: string; en: string; source: IntlDualCtaFallbackSource } {
  const es = (variantEs ?? "").trim();
  const en = (variantEn ?? "").trim();
  if (es || en) {
    return {
      es: es || INTL_DUAL_CTA_DEFAULT_VIP_LABEL_ES,
      en: en || INTL_DUAL_CTA_DEFAULT_VIP_LABEL_EN,
      source: "variant",
    };
  }
  return {
    es: INTL_DUAL_CTA_DEFAULT_VIP_LABEL_ES,
    en: INTL_DUAL_CTA_DEFAULT_VIP_LABEL_EN,
    source: "default",
  };
}

/** Resuelve mux, precio, labels y Stripe contract para visitor intl sin variante admin. */
export function resolveIntlDualCtaFallback(
  input: IntlDualCtaFallbackInput = {},
): IntlDualCtaResolvedFallback {
  const labels = pickVipLabels(input.variantVipLabelEs, input.variantVipLabelEn);
  const mux = pickMux(input.variantMuxPlaybackId, input.envMuxPlaybackId);
  const price = pickPrice(input.variantPriceUsd, input.envPriceUsd);
  const stripePrice = pickStripePriceId(input.variantStripePriceId);
  const stripeProductId =
    (input.variantStripeProductId ?? "").trim() || INTL_DUAL_CTA_DEFAULT_STRIPE_PRODUCT_ID;

  const adminSetupHints: string[] = [];
  if (mux.source !== "variant") {
    adminSetupHints.push("mux_playback_id");
  }
  if (price.source !== "variant") {
    adminSetupHints.push("price_usd");
  }
  if (stripePrice.source !== "variant") {
    adminSetupHints.push("stripe_price_id + stripe_product_id");
  }
  if (labels.source !== "variant") {
    adminSetupHints.push("vip_label_es / vip_label_en");
  }

  return {
    vipLabelEs: labels.es,
    vipLabelEn: labels.en,
    muxPlaybackId: mux.value,
    priceUsd: price.value,
    stripePriceId: stripePrice.value,
    stripeProductId,
    stripePromotionCodeId: INTL_DUAL_CTA_DEFAULT_STRIPE_PROMOTION_CODE_ID,
    stripeProductName: STRIPE_EXCLUSIVE_MEMBERSHIP_PRODUCT_NAME,
    postPaymentPath: INTL_DUAL_CTA_POST_PAYMENT_PATH,
    defaultVariantId: INTL_DUAL_CTA_DEFAULT_VARIANT_ID,
    sources: {
      vipLabel: labels.source,
      muxPlaybackId: mux.source,
      priceUsd: price.source,
      stripePriceId: stripePrice.source,
    },
    adminSetupHints,
  };
}

export function intlDualCtaDefaultVipLabel(lang: "es" | "en"): string {
  return lang === "es"
    ? INTL_DUAL_CTA_DEFAULT_VIP_LABEL_ES
    : INTL_DUAL_CTA_DEFAULT_VIP_LABEL_EN;
}

export interface VipStripeCheckoutOverrides {
  stripePriceIdOverride?: string;
  stripePromotionCodeId: string;
  /** Attribution cuando variant?.id ausente (sin assignment admin). */
  dualVariantId: string;
}

/**
 * Stripe price + promo del contrato intl (variante admin gana en price/id).
 *
 * IMPORTANTE: el promo_ canónico (`promo_1TquJs…`) solo aplica al Product
 * Exclusive Membership / price contrato. Si la variante A/B usa otro Price
 * (ej. us_test2 @ $18.94), adjuntar el promo rompe Checkout con
 * `coupon_applies_to_nothing` → fan_fallback. Solo mandamos promo cuando el
 * price efectivo es el del contrato.
 *
 * `stripePromoEnabled === false` (toggle admin): nunca aplica cupón, aunque
 * el price sea el del contrato o haya un promo_* guardado.
 */
export function resolveVipStripeCheckoutFromFallback(
  variant:
    | {
        stripePriceId?: string | null;
        stripePromotionCodeId?: string | null;
        /** false = sin cupón (toggle admin). null/undefined = legacy (habilitado). */
        stripePromoEnabled?: boolean | null;
        id?: string | null;
      }
    | null
    | undefined,
  intlFallback: IntlDualCtaResolvedFallback,
): VipStripeCheckoutOverrides {
  const fromVariant = (variant?.stripePriceId ?? "").trim();
  const stripePriceIdOverride = /^price_[A-Za-z0-9]+$/.test(fromVariant)
    ? fromVariant
    : intlFallback.stripePriceId ?? undefined;
  const variantId = (variant?.id ?? "").trim();

  // Toggle admin: sin cupón explícito — mismo product/price a precio full.
  if (variant?.stripePromoEnabled === false) {
    return {
      stripePriceIdOverride,
      stripePromotionCodeId: "",
      dualVariantId: variantId || intlFallback.defaultVariantId,
    };
  }

  // 1. Promo PROPIO de la variante (promo_*, migración 078). El cupón se crea SIN
  //    restricción de producto (api/admin/stripe-create-coupon) → aplica a su
  //    propio price. Gana sobre el promo del contrato: un descuento per-país
  //    definido por el admin siempre pesa más que el default global.
  const ownPromo = (variant?.stripePromotionCodeId ?? "").trim();
  if (/^promo_[A-Za-z0-9]+$/.test(ownPromo)) {
    return {
      stripePriceIdOverride,
      stripePromotionCodeId: ownPromo,
      dualVariantId: variantId || intlFallback.defaultVariantId,
    };
  }

  // 2. Promo del contrato: solo cuando el price efectivo es el del contrato
  //    (sino Stripe rechaza con coupon_applies_to_nothing → fan_fallback).
  const contractPriceId = (intlFallback.stripePriceId || INTL_DUAL_CTA_DEFAULT_STRIPE_PRICE_ID).trim();
  const promoAppliesToPrice =
    Boolean(stripePriceIdOverride) &&
    stripePriceIdOverride === contractPriceId &&
    Boolean(intlFallback.stripePromotionCodeId);
  return {
    stripePriceIdOverride,
    stripePromotionCodeId: promoAppliesToPrice
      ? intlFallback.stripePromotionCodeId
      : "",
    dualVariantId: variantId || intlFallback.defaultVariantId,
  };
}
